import { Injectable, inject } from '@angular/core';
import { nanoid } from 'nanoid';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService, QuerySignal } from './database.service';

export interface Invitation {
  id: string;
  token: string;
  created_by: string;
  created_at: number;  // INTEGER (epoch ms)
  updated_by: string;
  updated_at: number;  // INTEGER (epoch ms)
  data: string;  // JSON string
}

export interface InvitationData {
  email: string;
  role: 'admin' | 'user';
  status: 'pending' | 'accepted' | 'cancelled' | 'expired';
  expires_at: string;  // ISO 8601
  created_at: string;
  accepted_at?: string;
  cancelled_at?: string;
  cancelled_by?: string;
}

export interface InvitationViewModel extends Omit<Invitation, 'data'> {
  data: InvitationData;
  displayStatus: 'pending' | 'accepted' | 'cancelled' | 'expired';
  isExpired: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class InvitationService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);

  /**
   * Create query for all invitations
   * Pattern: RQS (Reactive Query Signal)
   */
  createQuery(): QuerySignal<InvitationViewModel[]> {
    return this.db.createQuery('invitations', () => this.getAll());
  }

  /**
   * Get all invitations with computed display properties
   * Pattern: Lazy expiration - check expires_at at runtime
   */
  async getAll(): Promise<InvitationViewModel[]> {
    await this.db.init();
    const rows = await this.db.exec(`
      SELECT id, token, created_by, created_at, updated_by, updated_at, data
      FROM invitations
      ORDER BY created_at DESC
    `);

    return rows.map((row: any) => this.toViewModel(row as Invitation));
  }

  /**
   * Get invitation by token
   */
  async getByToken(token: string): Promise<InvitationViewModel | null> {
    await this.db.init();
    const rows = await this.db.exec(
      'SELECT id, token, created_by, created_at, updated_by, updated_at, data FROM invitations WHERE token = ?',
      [token]
    );

    return rows.length > 0 ? this.toViewModel(rows[0] as Invitation) : null;
  }

  /**
   * Create new invitation
   * Pattern: Offline-first - saves locally, syncs in background
   */
  async create(
    email: string,
    role: 'admin' | 'user',
    createdBy: string,
    customerId: string
  ): Promise<InvitationViewModel> {
    const id = nanoid();
    const randomToken = nanoid(16);
    const token = `${customerId}:${id}:${randomToken}`;

    const now = this.clockSync.now();
    const expiresAt = new Date(now + 12 * 60 * 60 * 1000); // 12 hours

    const data: InvitationData = {
      email,
      role,
      status: 'pending',
      expires_at: expiresAt.toISOString(),
      created_at: new Date(now).toISOString()
    };

    await this.db.init();
    await this.db.run(
      `INSERT INTO invitations (id, token, created_by, created_at, updated_by, updated_at, data)
       VALUES (?, ?, ?, ?, ?, ?, ?)`,
      [id, token, createdBy, now, '', now, JSON.stringify(data)]
    );

    // Notify query watchers
    this.db.notifyDataChange('invitations');

    return this.toViewModel({ id, token, created_by: createdBy, created_at: now, updated_by: '', updated_at: now, data: JSON.stringify(data) });
  }

  /**
   * Cancel invitation
   * Pattern: Offline-first - updates locally, syncs in background
   */
  async cancel(id: string, cancelledBy: string): Promise<void> {
    await this.db.init();
    const rows = await this.db.exec(
      'SELECT data FROM invitations WHERE id = ?',
      [id]
    );

    if (rows.length === 0) return;

    const data: InvitationData = JSON.parse(rows[0].data);

    // Update status
    const now = this.clockSync.now();
    data.status = 'cancelled';
    data.cancelled_at = new Date(now).toISOString();
    data.cancelled_by = cancelledBy;

    await this.db.run(
      'UPDATE invitations SET data = ?, updated_by = ?, updated_at = ? WHERE id = ?',
      [JSON.stringify(data), cancelledBy, now, id]
    );

    // Notify query watchers
    this.db.notifyDataChange('invitations');
  }

  /**
   * Convert database row to view model
   * Pattern: Lazy expiration - compute status at runtime
   */
  private toViewModel(row: Invitation): InvitationViewModel {
    const data: InvitationData = JSON.parse(row.data);

    // Check source of truth (immutable expires_at)
    const isExpired = data.status === 'pending' &&
                      new Date(data.expires_at) < new Date(this.clockSync.now());

    const displayStatus = isExpired ? 'expired' : data.status;

    return {
      id: row.id,
      token: row.token,
      created_by: row.created_by,
      created_at: row.created_at,
      updated_by: row.updated_by,
      updated_at: row.updated_at,
      data,
      displayStatus,
      isExpired
    };
  }

  /**
   * Generate shareable URL for invitation
   */
  getInvitationUrl(token: string): string {
    // Use window.location.origin for current domain
    return `${window.location.origin}/accept-invitation?token=${token}`;
  }
}
